<!-- src/components/Inventario/Reportes/ScrapAlmacenLaser/components/SALSelectorPeriodo.vue -->
<template>
  <div class="sal-selector">
    <!-- Periodo A -->
    <div class="sal-selector__periodo">
      <span class="sal-selector__badge sal-selector__badge--a">A</span>
      <div class="sal-selector__grupo">
        <label class="sal-selector__label">Periodo A</label>
        <div class="sal-selector__rango">
          <input
            type="date"
            v-model="localA.fecha_inicio"
            :max="localA.fecha_fin || undefined"
            @change="emitirA"
          />
          <span class="sal-selector__sep">—</span>
          <input
            type="date"
            v-model="localA.fecha_fin"
            :min="localA.fecha_inicio || undefined"
            @change="emitirA"
          />
        </div>
      </div>
    </div>

    <!-- Periodo B -->
    <div class="sal-selector__periodo">
      <span class="sal-selector__badge sal-selector__badge--b">B</span>
      <div class="sal-selector__grupo">
        <label class="sal-selector__label">Periodo B</label>
        <div class="sal-selector__rango">
          <input
            type="date"
            v-model="localB.fecha_inicio"
            :max="localB.fecha_fin || undefined"
            @change="emitirB"
          />
          <span class="sal-selector__sep">—</span>
          <input
            type="date"
            v-model="localB.fecha_fin"
            :min="localB.fecha_inicio || undefined"
            @change="emitirB"
          />
        </div>
      </div>
    </div>

    <!-- Multiselect de productos -->
    <div class="sal-selector__bloque sal-selector__bloque--productos" ref="dropdownRef">
      <label class="sal-selector__label">Productos</label>
      <div class="sal-multiselect">
        <button type="button" class="sal-multiselect__btn" @click="abierto = !abierto">
          <span>{{ textoBoton }}</span>
          <span
            class="sal-multiselect__caret"
            :class="{ 'sal-multiselect__caret--abierto': abierto }"
            >▾</span
          >
        </button>
        <div v-if="abierto" class="sal-multiselect__panel">
          <label class="sal-multiselect__opcion sal-multiselect__opcion--todos">
            <input type="checkbox" :checked="mostrarTodos" @change="toggleTodos" />
            Todos los productos
          </label>
          <div v-if="cargandoProductos" class="sal-multiselect__vacio">Cargando…</div>
          <div class="sal-multiselect__lista" v-else>
            <label
              v-for="p in catalogoProductos"
              :key="p.id"
              class="sal-multiselect__opcion"
            >
              <input
                type="checkbox"
                :value="p.id"
                v-model="seleccionadosLocal"
                @change="emitirProductos"
              />
              {{ p.nombre }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  periodoA: { type: Object, required: true }, // { fecha_inicio, fecha_fin }
  periodoB: { type: Object, required: true },
  productosSeleccionados: { type: Array, default: () => [] },
  mostrarTodos: { type: Boolean, default: false },
  catalogoProductos: { type: Array, default: () => [] },
  cargandoProductos: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:periodoA",
  "update:periodoB",
  "update:productos-seleccionados",
  "update:mostrar-todos",
]);

const localA = reactive({ ...props.periodoA });
const localB = reactive({ ...props.periodoB });
const seleccionadosLocal = ref([...props.productosSeleccionados]);
const abierto = ref(false);
const dropdownRef = ref(null);

watch(
  () => props.periodoA,
  (v) => Object.assign(localA, v)
);
watch(
  () => props.periodoB,
  (v) => Object.assign(localB, v)
);
watch(
  () => props.productosSeleccionados,
  (v) => {
    seleccionadosLocal.value = [...v];
  }
);

const MESES = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/**
 * Formatea una fecha ISO ('2026-01-05') a 'dd mes' sin pasar por objetos
 * Date (evita el corrimiento de un día por zona horaria al usar new Date()
 * con un string 'YYYY-MM-DD', que JS interpreta como UTC medianoche).
 */
function formatearDia(fechaIso) {
  const [, mes, dia] = fechaIso.split("-").map(Number);
  return `${dia} ${MESES[mes - 1]}`;
}

function etiqueta(fecha_inicio, fecha_fin) {
  if (!fecha_inicio || !fecha_fin) return "";
  if (fecha_inicio > fecha_fin) return "Rango inválido";
  const anio = fecha_fin.split("-")[0];
  return `${formatearDia(fecha_inicio)} – ${formatearDia(fecha_fin)} ${anio}`;
}

const textoBoton = computed(() => {
  if (!seleccionadosLocal.value.length) return "Todos los productos";
  if (seleccionadosLocal.value.length === 1) {
    const p = props.catalogoProductos.find((p) => p.id === seleccionadosLocal.value[0]);
    return p?.nombre || "1 producto";
  }
  return `${seleccionadosLocal.value.length} productos seleccionados`;
});

function emitirA() {
  if (
    localA.fecha_inicio &&
    localA.fecha_fin &&
    localA.fecha_inicio <= localA.fecha_fin
  ) {
    emit("update:periodoA", { ...localA });
  }
}
function emitirB() {
  if (
    localB.fecha_inicio &&
    localB.fecha_fin &&
    localB.fecha_inicio <= localB.fecha_fin
  ) {
    emit("update:periodoB", { ...localB });
  }
}
function emitirProductos() {
  emit("update:mostrar-todos", false); // selección específica → quitar "todos"
  emit("update:productos-seleccionados", [...seleccionadosLocal.value]);
}
function toggleTodos(e) {
  const checked = e.target.checked;
  seleccionadosLocal.value = []; // limpiar selección específica
  emit("update:mostrar-todos", checked);
  emit("update:productos-seleccionados", []);
}

function manejarClickFuera(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) abierto.value = false;
}
onMounted(() => document.addEventListener("click", manejarClickFuera));
onBeforeUnmount(() => document.removeEventListener("click", manejarClickFuera));
</script>

<style scoped>
.sal-selector__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1.2rem; /* alinear con los inputs */
}
.sal-selector__badge--a {
  background: #7b2637;
  color: #fff;
}
.sal-selector__badge--b {
  background: #c9748a;
  color: #fff;
}

.sal-selector__grupo {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
</style>
